import { Checkbox, Pagination, Spin, Table, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ListError, useIsNarrow } from './shared';
import styles from './styles.module.scss';

export interface DataColumn<T> {
  key: string;
  title: string;
  /** Bề rộng cột ở dạng bảng (px). Be Vietnam Pro rộng hơn Inter nên đặt dư chứ đừng sát. */
  width: number;
  /**
   * Vị trí ở dạng thẻ (điện thoại): 'head' = đầu thẻ, không nhãn · 'actions' = cuối thẻ.
   * Không ghi thì là một dòng "nhãn: giá trị".
   */
  slot?: 'head' | 'actions';
  render: (row: T) => React.ReactNode;
}

interface Selection<T> {
  selected: T[];
  max: number;
  onChange: (rows: T[]) => void;
  /** Dòng không chọn được (vd. đã xác nhận rồi). */
  disabled?: (row: T) => boolean;
  /** Tên người của dòng, để ô tích có nhãn đọc được. */
  label: (row: T) => string;
}

interface Props<T> {
  columns: DataColumn<T>[];
  items: T[];
  rowKey: (row: T) => number;
  loading: boolean;
  error: string;
  onRetry: () => void;
  empty: React.ReactNode;
  selection: Selection<T>;
  /** Danh sách không có việc hàng loạt (vd. "Đã xác nhận"): bỏ hẳn cột / ô tích. */
  noSelection?: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}

/**
 * Danh sách của trang Xác nhận nhân viên: máy tính là bảng (cột Thao tác ghim phải, bảng tự cuộn
 * trong khung riêng khi hẹp); điện thoại (< 768px) là các thẻ xếp dọc, không phải cuộn ngang.
 */
function DataView<T>({
  columns,
  items,
  rowKey,
  loading,
  error,
  onRetry,
  empty,
  selection,
  noSelection,
  page,
  pageSize,
  total,
  onPage,
}: Props<T>) {
  const narrow = useIsNarrow();
  const selectedKeys = selection.selected.map(rowKey);

  const commit = (rows: T[]) => {
    if (rows.length > selection.max) {
      notification.warning({ message: `Mỗi lần chỉ làm được tối đa ${selection.max} người.` });
    }
    selection.onChange(rows.slice(0, selection.max));
  };

  if (error) {
    return (
      <div className="custom-table">
        <ListError text={error} onRetry={onRetry} />
      </div>
    );
  }

  if (!narrow) {
    const tableColumns: ColumnsType<T> = columns.map((column) => ({
      key: column.key,
      title: column.title,
      width: column.width,
      fixed: column.slot === 'actions' ? ('right' as const) : undefined,
      render: (_: unknown, row: T) => column.render(row),
    }));
    const width = columns.reduce((sum, column) => sum + column.width, noSelection ? 0 : 48);
    return (
      <div className="custom-table">
        <Table<T>
          rowKey={rowKey}
          size="middle"
          loading={loading}
          columns={tableColumns}
          dataSource={items}
          scroll={items.length ? { x: width } : undefined}
          locale={{ emptyText: empty }}
          rowSelection={
            noSelection
              ? undefined
              : {
                  columnWidth: 48,
                  preserveSelectedRowKeys: true,
                  selectedRowKeys: selectedKeys,
                  getCheckboxProps: (row) => ({
                    disabled: !!selection.disabled?.(row),
                    'aria-label': `Chọn ${selection.label(row)}`,
                  }),
                  onChange: (_, rows) =>
                    commit(rows.filter(Boolean).filter((row) => !selection.disabled?.(row))),
                }
          }
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: false,
            hideOnSinglePage: true,
            onChange: onPage,
          }}
        />
      </div>
    );
  }

  const head = columns.filter((column) => column.slot === 'head');
  const actions = columns.filter((column) => column.slot === 'actions');
  const lines = columns.filter((column) => !column.slot);
  const selectable = noSelection ? [] : items.filter((row) => !selection.disabled?.(row));
  const pageKeys = selectable.map(rowKey);
  const pickedOnPage = pageKeys.filter((key) => selectedKeys.includes(key)).length;

  const toggleRow = (row: T, checked: boolean) => {
    const key = rowKey(row);
    const rest = selection.selected.filter((item) => rowKey(item) !== key);
    commit(checked ? [...rest, row] : rest);
  };

  const togglePage = (checked: boolean) => {
    const rest = selection.selected.filter((item) => !pageKeys.includes(rowKey(item)));
    commit(checked ? [...rest, ...selectable] : rest);
  };

  return (
    <Spin spinning={loading}>
      {items.length === 0 ? (
        <div className={styles.cardEmpty}>{loading ? null : empty}</div>
      ) : (
        <>
          {selectable.length > 0 && (
            <Checkbox
              className={styles.cardPickAll}
              checked={pickedOnPage > 0 && pickedOnPage === pageKeys.length}
              indeterminate={pickedOnPage > 0 && pickedOnPage < pageKeys.length}
              onChange={(event) => togglePage(event.target.checked)}
            >
              Chọn cả {selectable.length} người ở trang này
            </Checkbox>
          )}
          <ul className={styles.cards}>
            {items.map((row) => {
              const key = rowKey(row);
              const disabled = !!selection.disabled?.(row);
              return (
                <li key={key} className={styles.rowCard}>
                  <div className={styles.rowCardHead}>
                    {!noSelection && (
                      <Checkbox
                        checked={selectedKeys.includes(key)}
                        disabled={disabled}
                        aria-label={`Chọn ${selection.label(row)}`}
                        onChange={(event) => toggleRow(row, event.target.checked)}
                      />
                    )}
                    <div className={styles.rowCardHeadBody}>
                      {head.map((column) => (
                        <div key={column.key}>{column.render(row)}</div>
                      ))}
                    </div>
                  </div>
                  <dl className={styles.rowCardLines}>
                    {lines.map((column) => (
                      <div key={column.key} className={styles.rowCardLine}>
                        <dt>{column.title}</dt>
                        <dd>{column.render(row)}</dd>
                      </div>
                    ))}
                  </dl>
                  {actions.map((column) => (
                    <div key={column.key} className={styles.rowCardActions}>
                      {column.render(row)}
                    </div>
                  ))}
                </li>
              );
            })}
          </ul>
          {total > pageSize && (
            <div className={styles.cardPager}>
              <Pagination
                simple
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger={false}
                onChange={onPage}
              />
            </div>
          )}
        </>
      )}
    </Spin>
  );
}

export default DataView;
